import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { 
  Loader2, 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Calendar,
  Briefcase,
  Euro,
  FileText,
  Star,
  UserPlus,
  XCircle,
  CheckCircle,
  CalendarPlus,
  Send,
  Camera,
  Upload,
  Cake,
  Award,
  Sparkles
} from "lucide-react";
import type { Applicant, ApplicationStage, ApplicantStageHistory } from "@/types/applicant";
import { stageLabels, stageColors, stageOrder } from "@/types/applicant";
import ApplicantDataForm from "@/components/applicants/ApplicantDataForm";
import ApplicantStageManager from "@/components/applicants/ApplicantStageManager";
import ApplicantHistory from "@/components/applicants/ApplicantHistory";
import ApplicantDocuments from "@/components/applicants/ApplicantDocuments";
import InterviewInvitationDialog from "@/components/applicants/InterviewInvitationDialog";
import OfferEmailDialog from "@/components/applicants/OfferEmailDialog";
import RejectionEmailDialog from "@/components/applicants/RejectionEmailDialog";
import { EmailCorrespondenceTab } from "@/components/EmailCorrespondenceTab";
import ConfirmAlternativeDateDialog from "@/components/applicants/ConfirmAlternativeDateDialog";

interface PendingAltInvitation {
  id: string;
  alternative_date: string;
  alternative_time: string;
  alternative_notes: string | null;
  location: string | null;
}

const ApplicantDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading, profile, userRole } = useAuth();
  const [applicant, setApplicant] = useState<Applicant | null>(null);
  const [history, setHistory] = useState<ApplicantStageHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [pendingAlt, setPendingAlt] = useState<PendingAltInvitation | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && id) {
      fetchApplicant();
      fetchHistory();
      fetchProfilePhoto();
      fetchPendingAlternative();
    }
  }, [user, id]);

  const fetchPendingAlternative = async () => {
    if (!id) return;
    const { data } = await supabase
      .from("applicant_interview_invitations")
      .select("id, alternative_date, alternative_time, alternative_notes, location")
      .eq("applicant_id", id)
      .eq("response_status", "alternative_proposed")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setPendingAlt(data as PendingAltInvitation | null);
  };


  const fetchProfilePhoto = async () => {
    if (!id) return;
    
    try {
      // First check if applicant has a profile_image_url set
      const { data: applicantData } = await supabase
        .from("applicants")
        .select("profile_image_url")
        .eq("id", id)
        .single();
      
      if (applicantData?.profile_image_url) {
        const profileUrl = applicantData.profile_image_url;
        
        // Check if it's already a full URL (legacy data) or a storage path
        if (profileUrl.startsWith('http://') || profileUrl.startsWith('https://')) {
          // It's already a full URL, use it directly
          setAvatarUrl(profileUrl);
          return;
        }
        
        // It's a storage path, get signed URL
        const { data: urlData } = await supabase.storage
          .from("applicant-documents")
          .createSignedUrl(profileUrl, 3600);
        
        if (urlData?.signedUrl) {
          setAvatarUrl(urlData.signedUrl);
          return;
        }
      }
      
      // If no profile_image_url or signed URL failed, check for photo documents
      // First check for uploaded photos with category "photo"
      const { data: photoDoc } = await supabase
        .from("applicant_documents")
        .select("file_path")
        .eq("applicant_id", id)
        .eq("category", "photo")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (photoDoc?.file_path) {
        const { data: urlData } = await supabase.storage
          .from("applicant-documents")
          .createSignedUrl(photoDoc.file_path, 3600);
        
        if (urlData?.signedUrl) {
          setAvatarUrl(urlData.signedUrl);
          
          // Also update the applicant's profile_image_url for faster future lookups
          await supabase
            .from("applicants")
            .update({ profile_image_url: photoDoc.file_path })
            .eq("id", id);
          return;
        }
      }
      
      // Fallback: check for extracted profile photo
      const { data: profilePhoto } = await supabase
        .from("applicant_documents")
        .select("file_path")
        .eq("applicant_id", id)
        .eq("is_profile_photo", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (profilePhoto?.file_path) {
        const { data: urlData } = await supabase.storage
          .from("applicant-documents")
          .createSignedUrl(profilePhoto.file_path, 3600);
        
        if (urlData?.signedUrl) {
          setAvatarUrl(urlData.signedUrl);
          
          // Also update the applicant's profile_image_url
          await supabase
            .from("applicants")
            .update({ profile_image_url: profilePhoto.file_path })
            .eq("id", id);
        }
      }
    } catch (error) {
      console.error("Error fetching profile photo:", error);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user || !id) return;
    
    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte nur Bilddateien hochladen");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bild zu groß. Maximale Größe: 5MB");
      return;
    }
    
    setUploadingAvatar(true);
    
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${id}/profile_${Date.now()}.${fileExt}`;
      
      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("applicant-documents")
        .upload(fileName, file);
      
      if (uploadError) throw uploadError;
      
      // Update applicant's profile_image_url
      const { error: updateError } = await supabase
        .from("applicants")
        .update({ profile_image_url: fileName })
        .eq("id", id);
      
      if (updateError) throw updateError;
      
      // Also create a document record
      await supabase
        .from("applicant_documents")
        .insert({
          applicant_id: id,
          file_name: `Profilfoto_${file.name}`,
          file_path: fileName,
          file_size: file.size,
          mime_type: file.type,
          category: "photo",
          description: "Profilfoto",
          uploaded_by: user.id,
          is_profile_photo: true,
        });
      
      // Get signed URL for display
      const { data: urlData } = await supabase.storage
        .from("applicant-documents")
        .createSignedUrl(fileName, 3600);
      
      if (urlData?.signedUrl) {
        setAvatarUrl(urlData.signedUrl);
      }
      
      toast.success("Profilfoto hochgeladen");
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error("Fehler beim Hochladen des Profilfotos");
    } finally {
      setUploadingAvatar(false);
      event.target.value = "";
    }
  };

  const fetchApplicant = async () => {
    try {
      const { data, error } = await supabase
        .from("applicants")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      setApplicant(data as unknown as Applicant);
    } catch (error) {
      console.error("Error fetching applicant:", error);
      toast.error("Bewerber nicht gefunden");
      navigate("/applicants");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const { data, error } = await supabase
        .from("applicant_stage_history")
        .select("*")
        .eq("applicant_id", id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setHistory((data || []) as unknown as ApplicantStageHistory[]);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  const handleStageChange = async (newStage: ApplicationStage, notes?: string) => {
    if (!applicant) return;

    try {
      const { error: updateError } = await supabase
        .from("applicants")
        .update({ current_stage: newStage })
        .eq("id", applicant.id);

      if (updateError) throw updateError;

      await supabase.from("applicant_stage_history").insert({
        applicant_id: applicant.id,
        from_stage: applicant.current_stage,
        to_stage: newStage,
        changed_by: profile?.full_name || "System",
        notes,
      });

      toast.success(`Phase geändert zu "${stageLabels[newStage]}"`);
      fetchApplicant();
      fetchHistory();
    } catch (error) {
      console.error("Error changing stage:", error);
      toast.error("Fehler beim Ändern der Phase");
    }
  };

  const handleConvertToEmployee = async () => {
    if (!applicant) return;

    try {
      toast.info("Konvertierung läuft...", { id: "convert-progress" });
      
      // Generate new employee ID upfront
      const newEmployeeId = crypto.randomUUID();
      
      // Copy profile image to employee storage if exists
      let employeeProfileImageUrl: string | null = null;
      
      if ((applicant as any).profile_image_url) {
        try {
          // Download from applicant storage
          const { data: imageData } = await supabase.storage
            .from("applicant-documents")
            .download((applicant as any).profile_image_url);
          
          if (imageData) {
            const fileName = (applicant as any).profile_image_url.split("/").pop() || "profile.jpg";
            const newPath = `${newEmployeeId}/${fileName}`;
            
            // Upload to employee-profiles bucket
            const { error: uploadError } = await supabase.storage
              .from("employee-profiles")
              .upload(newPath, imageData);
            
            if (!uploadError) {
              employeeProfileImageUrl = newPath;
            }
          }
        } catch (imgError) {
          console.error("Error copying profile image:", imgError);
        }
      }
      
      // Create employee from applicant data with all fields
      const { data: employee, error: createError } = await supabase
        .from("employees")
        .insert({
          id: newEmployeeId,
          name: `${applicant.first_name} ${applicant.last_name}`,
          position: applicant.desired_position || "Neue Position",
          phone: applicant.phone,
          mobile: applicant.phone,
          private_email: applicant.email,
          address_street: applicant.address_street,
          address_zip: applicant.address_zip,
          address_city: applicant.address_city,
          address_country: applicant.address_country,
          birth_date: applicant.birth_date,
          created_by: user?.id,
          profile_image_url: employeeProfileImageUrl,
          // Additional fields from applicant
          drivers_license_classes: applicant.drivers_license_classes,
          special_certifications: applicant.special_certifications,
          security_clearance_valid_until: applicant.security_clearance_valid_until,
          notes: applicant.qualifications || applicant.notes,
        })
        .select()
        .single();

      if (createError) throw createError;

      // Copy all applicant documents to employee documents
      const { data: applicantDocs } = await supabase
        .from("applicant_documents")
        .select("*")
        .eq("applicant_id", applicant.id);

      if (applicantDocs && applicantDocs.length > 0) {
        let copiedCount = 0;
        let failedCount = 0;

        for (const doc of applicantDocs) {
          try {
            // Download from applicant-documents storage
            const { data: fileData, error: downloadError } = await supabase.storage
              .from("applicant-documents")
              .download(doc.file_path);

            if (downloadError || !fileData) {
              console.error(`Error downloading document ${doc.file_name}:`, downloadError);
              failedCount++;
              continue;
            }

            // Generate new path for employee storage
            const newPath = `${newEmployeeId}/${doc.file_name}`;

            // Upload to employee-documents storage
            const { error: uploadError } = await supabase.storage
              .from("employee-documents")
              .upload(newPath, fileData);

            if (uploadError) {
              console.error(`Error uploading document ${doc.file_name}:`, uploadError);
              failedCount++;
              continue;
            }

            // Create employee_documents record
            await supabase.from("employee_documents").insert({
              employee_id: newEmployeeId,
              file_name: doc.file_name,
              file_path: newPath,
              file_size: doc.file_size,
              mime_type: doc.mime_type,
              category: doc.category,
              description: doc.description,
              uploaded_by: user?.id || doc.uploaded_by,
            });

            copiedCount++;
          } catch (docError) {
            console.error(`Error processing document ${doc.file_name}:`, docError);
            failedCount++;
          }
        }

        if (copiedCount > 0) {
          toast.success(`${copiedCount} Dokument(e) übertragen`, { id: "docs-copied" });
        }
        if (failedCount > 0) {
          toast.warning(`${failedCount} Dokument(e) konnten nicht übertragen werden`);
        }
      }

      // Update applicant as converted
      const { error: updateError } = await supabase
        .from("applicants")
        .update({
          current_stage: "hired",
          converted_to_employee_id: employee.id,
        })
        .eq("id", applicant.id);

      if (updateError) throw updateError;

      // Add stage history
      await supabase.from("applicant_stage_history").insert({
        applicant_id: applicant.id,
        from_stage: applicant.current_stage,
        to_stage: "hired",
        changed_by: profile?.full_name || "System",
        notes: "In Mitarbeiter umgewandelt",
      });

      toast.success("Bewerber erfolgreich in Mitarbeiter umgewandelt", { id: "convert-progress" });
      navigate(`/employee/${employee.id}`);
    } catch (error) {
      console.error("Error converting to employee:", error);
      toast.error("Fehler bei der Umwandlung", { id: "convert-progress" });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!applicant) {
    return null;
  }

  const isConverted = !!applicant.converted_to_employee_id;

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/applicants")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zur Übersicht
        </Button>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-80 space-y-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <div className="relative group mb-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                    <Avatar className="h-24 w-24 cursor-pointer border-2 border-primary/20 hover:border-primary transition-colors">
                      {avatarUrl ? (
                        <AvatarImage src={avatarUrl} alt={`${applicant.first_name} ${applicant.last_name}`} />
                      ) : null}
                      <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                        {applicant.first_name?.[0]}{applicant.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingAvatar}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {uploadingAvatar ? (
                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                      ) : (
                        <Camera className="h-6 w-6 text-white" />
                      )}
                    </button>
                  </div>
                  <h2 className="text-xl font-bold">
                    {applicant.first_name} {applicant.last_name}
                  </h2>
                  {applicant.desired_position && (
                    <p className="text-muted-foreground">{applicant.desired_position}</p>
                  )}
                  <Badge
                    className={`mt-2 ${stageColors[applicant.current_stage]} text-white`}
                  >
                    {stageLabels[applicant.current_stage]}
                  </Badge>
                  {applicant.rating && (
                    <div className="flex items-center gap-1 mt-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${
                            i < applicant.rating!
                              ? "fill-amber-400 text-amber-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-6 space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${applicant.email}`} className="hover:underline">
                      {applicant.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${applicant.phone}`} className="hover:underline">
                      {applicant.phone}
                    </a>
                  </div>
                  {applicant.address_city && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>
                        {applicant.address_zip} {applicant.address_city}
                      </span>
                    </div>
                  )}
                  {applicant.birth_date && (
                    <div className="flex items-center gap-2">
                      <Cake className="h-4 w-4 text-muted-foreground" />
                      <span>
                        {(() => {
                          const birthDate = new Date(applicant.birth_date);
                          const today = new Date();
                          let age = today.getFullYear() - birthDate.getFullYear();
                          const monthDiff = today.getMonth() - birthDate.getMonth();
                          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                            age--;
                          }
                          return `${age} Jahre`;
                        })()}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      Eingegangen: {new Date(applicant.created_at).toLocaleDateString("de-DE")}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Career Summary Card - AI-extracted highlights */}
            {(applicant as any).career_summary && (
              <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    Karriere-Highlights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5">
                    {((applicant as any).career_summary as string).split('\n').filter(Boolean).map((point: string, idx: number) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <Award className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                        <span>{point.replace(/^[•\-]\s*/, '')}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {pendingAlt && !isConverted && (
              <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-amber-900 dark:text-amber-200">
                    <CalendarPlus className="h-4 w-4" />
                    Alternativtermin vorgeschlagen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm space-y-1">
                    <p>
                      <strong>Datum:</strong>{" "}
                      {new Date(pendingAlt.alternative_date).toLocaleDateString("de-DE", {
                        weekday: "long", year: "numeric", month: "long", day: "numeric",
                      })}
                    </p>
                    <p><strong>Uhrzeit:</strong> {String(pendingAlt.alternative_time).slice(0, 5)} Uhr</p>
                    {pendingAlt.alternative_notes && (
                      <p className="text-muted-foreground">
                        <strong>Hinweis:</strong> {pendingAlt.alternative_notes}
                      </p>
                    )}
                  </div>
                  <ConfirmAlternativeDateDialog
                    invitationId={pendingAlt.id}
                    applicantName={`${applicant.first_name} ${applicant.last_name}`}
                    applicantEmail={applicant.email}
                    alternativeDate={pendingAlt.alternative_date}
                    alternativeTime={pendingAlt.alternative_time}
                    location={pendingAlt.location}
                    alternativeNotes={pendingAlt.alternative_notes}
                    onSuccess={() => {
                      fetchPendingAlternative();
                      fetchApplicant();
                      fetchHistory();
                    }}
                  />
                </CardContent>
              </Card>
            )}



            {!isConverted && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Aktionen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {applicant.current_stage !== "hired" &&
                    applicant.current_stage !== "rejected" && (
                      <>
                        {/* Interview Invitation */}
                        <InterviewInvitationDialog
                          applicant={applicant}
                          onSuccess={() => {
                            fetchApplicant();
                            fetchHistory();
                          }}
                        />
                        
                        {/* Job Offer */}
                        <OfferEmailDialog
                          applicant={applicant}
                          onSuccess={() => {
                            fetchApplicant();
                            fetchHistory();
                          }}
                        />
                        
                        <Button
                          className="w-full"
                          onClick={handleConvertToEmployee}
                          disabled={applicant.current_stage !== "offer"}
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          In Mitarbeiter umwandeln
                        </Button>
                        
                        {/* Rejection (with email) */}
                        <RejectionEmailDialog
                          applicant={applicant}
                          onSuccess={() => {
                            fetchApplicant();
                            fetchHistory();
                          }}
                        />

                        {/* Applicant withdrew on their own - no email */}
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            if (window.confirm("Bewerber hat selbst abgesagt? Der Bewerber wird ohne E-Mail-Versand in 'Abgesagt' verschoben.")) {
                              handleStageChange("rejected", "Bewerber hat selbst abgesagt");
                            }
                          }}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Bewerber hat abgesagt
                        </Button>
                      </>
                    )}
                </CardContent>
              </Card>
            )}

            {isConverted && (
              <Card className="border-green-500">
                <CardContent className="pt-6 text-center">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="font-medium">Als Mitarbeiter eingestellt</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() =>
                      navigate(`/employee/${applicant.converted_to_employee_id}`)
                    }
                  >
                    Zum Mitarbeiter
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <Tabs defaultValue="data">
              <TabsList className="mb-4">
                <TabsTrigger value="data">Daten</TabsTrigger>
                <TabsTrigger value="documents">Dokumente</TabsTrigger>
                <TabsTrigger value="stage">Bewerbungsprozess</TabsTrigger>
                <TabsTrigger value="emails">E-Mails</TabsTrigger>
                <TabsTrigger value="history">Verlauf</TabsTrigger>
              </TabsList>

              <TabsContent value="data">
                <ApplicantDataForm
                  applicant={applicant}
                  onUpdate={fetchApplicant}
                />
              </TabsContent>

              <TabsContent value="documents">
                <ApplicantDocuments applicantId={applicant.id} />
              </TabsContent>

              <TabsContent value="stage">
                <ApplicantStageManager
                  applicant={applicant}
                  onStageChange={handleStageChange}
                />
              </TabsContent>

              <TabsContent value="emails">
                <EmailCorrespondenceTab
                  applicantId={applicant.id}
                  defaultEmail={applicant.email}
                  recipientName={`${applicant.first_name} ${applicant.last_name}`}
                />
              </TabsContent>

              <TabsContent value="history">
                <ApplicantHistory history={history} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
  );
};

export default ApplicantDetailPage;
